import { useMutation, useQuery } from "@apollo/client";
import OpenInNew from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Link as MuiLink,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { Dialog } from "components/Dialog";
import { ResolutionStatusChip } from "components/ResolutionStatusChip";
import { RightDrawerShell } from "components/RightDrawerShell/RightDrawerShell";
import { StatusChip } from "components/StatusChip";
import {
  LQG_THRESHOLD_ACUTE_HAZARDOUS_LB,
  LQG_THRESHOLD_HAZARDOUS_LB,
} from "encamp-shared/src/constants/wasteGeneratorStatusThresholds";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  GeneratorStatusExceedanceDirection,
  GeneratorStatusExceedanceRiskLevel,
  RcraInfoFederalWasteGeneratorStatus,
  ResolutionStatus,
} from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { MouseEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  CreateGeneratorStatusChangeSubmissionsDialog,
  GeneratorStatusChangeFacility,
} from "./CreateGeneratorStatusChangeSubmissionsDialog";
import { DRAWER_PARAM, toDrawerParam } from "./drawerDeepLink";
import { formatPeriodMonth, formatPeriodSpan } from "./periodFormat";
import { stageLabel } from "./stages";

type LinkedSubmissionSummary = {
  id: string;
  title: string;
  submissionType?: string;
  status: string;
  dueDate?: string | null;
};

export type ExceedanceForDrawer = {
  facilityId: string;
  tenantId: string;
  facilityName: string;
  organization: string;
  state: string | null;
  source: string;
  periodStart: string;
  /** Latest month of the status run; equals periodStart for one month. */
  lastPeriodStart?: string | null;
  reportedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus?: RcraInfoFederalWasteGeneratorStatus | null;
  changeExplanation: string;
  resolutionStatus: ResolutionStatus;
  direction?: GeneratorStatusExceedanceDirection | null;
  sustainedMonths?: number | null;
  /** Detected mismatching months folded into the run; null until the next sweep. */
  mismatchMonths?: number | null;
  /** Derived compliance-risk signal; UPWARD rows only. */
  riskLevel?: GeneratorStatusExceedanceRiskLevel | null;
  detectedAt?: string | null;
  validatedAt?: string | null;
  staffAssigneeName?: string | null;
  openSubmission?: LinkedSubmissionSummary | null;
  linkedSubmission?: LinkedSubmissionSummary | null;
};

type ExceedanceKey = {
  facilityId: string;
  source: string;
  periodStart: string;
  reportedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedFederalStatus: RcraInfoFederalWasteGeneratorStatus | null;
};

const DETAIL_QUERY = gql(`
  query GeneratorStatusExceedanceDetail($facilityId: ID!, $source: String!, $periodStart: Date!) {
    generatorStatusExceedanceDetail(facilityId: $facilityId, source: $source, periodStart: $periodStart) {
      facilityId
      source
      periodStart
      determinationFactors
      resolution {
        id
        status
        resolutionType
        note
        resolvedAt
      }
      events {
        id
        eventType
        status
        resolutionType
        note
        actorId
        actorName
        createdAt
      }
      sources {
        source
        federalStatus
        totalHazardousLbs
        acuteLbs
      }
      calculatedMonths {
        periodStart
        federalStatus
        totalHazardousLbs
        acuteLbs
        mismatch
        inRun
        siblingPeriodStart
      }
      wadmByMonth {
        periodStart
        totalWeightLbs
        itemCount
        wadmGeneratorStatus
      }
    }
  }
`);

const DISMISS_MUTATION = gql(`
  mutation DismissGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $reason: String!
  ) {
    dismissGeneratorStatusExceedances(exceedances: $exceedances, reason: $reason) {
      id
      status
    }
  }
`);

const MARK_RESOLVED_MUTATION = gql(`
  mutation MarkGeneratorStatusExceedancesResolved(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
    $submissionId: ID
  ) {
    markGeneratorStatusExceedancesResolved(
      exceedances: $exceedances
      note: $note
      submissionId: $submissionId
    ) {
      id
      status
    }
  }
`);

const LINKABLE_SUBMISSIONS_QUERY = gql(`
  query GeneratorStatusExceedanceLinkableSubmissions($facilityId: ID!, $search: String) {
    generatorStatusExceedanceLinkableSubmissions(facilityId: $facilityId, search: $search) {
      id
      title
      submissionType
      status
      dueDate
      createdAt
      facilityName
      assigneeName
    }
  }
`);

const REOPEN_MUTATION = gql(`
  mutation ReopenGeneratorStatusExceedances(
    $exceedances: [GeneratorStatusExceedanceKeyInput!]!
    $note: String
  ) {
    reopenGeneratorStatusExceedances(exceedances: $exceedances, note: $note) {
      id
      status
    }
  }
`);

const VALIDATE_MUTATION = gql(`
  mutation ValidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    validateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const UNVALIDATE_MUTATION = gql(`
  mutation UnvalidateGeneratorStatusExceedance($exceedances: [GeneratorStatusExceedanceKeyInput!]!) {
    unvalidateGeneratorStatusExceedances(exceedances: $exceedances) {
      id
      status
    }
  }
`);

const REFETCH = ["StaffGeneratorStatusChanges", "GeneratorStatusExceedanceDetail"];

/** Months shown in the calculated-status table before the "show all" toggle. */
const COLLAPSED_MONTHS = 8;

function statusLabel(status: string): string {
  return prettyPrintEnumValue(status);
}

/**
 * Distinct chip color per lifecycle stage so each reads uniquely,
 * matching the grid: Needs Validation = error/red, Validated · Ready for Pickup =
 * warning/orange. Undefined falls back to the chip's status default (In Flight /
 * PENDING = info/blue, Resolved = success/green, Ignored = grey).
 */
function stageRole(status: ResolutionStatus, validated: boolean): "warning" | "error" | undefined {
  if (status === ResolutionStatus.Unresolved) return validated ? "warning" : "error";
  return undefined;
}

/** Human label for a waste source. */
function sourceLabel(source: string): string {
  if (source === "eManifest") return "e-Manifest";
  if (source === "vendor") return "Vendor";
  if (source === "document") return "Document";
  return prettyPrintEnumValue(source);
}

/**
 * True when a month's LQG determination rests on acute waste alone: generating
 * >= 2.2 lbs (1 kg) of acutely hazardous waste in a month makes the facility LQG
 * regardless of total volume (40 CFR 262.13). Not flagged when total tonnage
 * independently reaches the LQG threshold — acute isn't the deciding factor there.
 * Mirrors `federalThresholdCalculator` in process-manifests.
 */
function isAcuteDriven(
  federalStatus: RcraInfoFederalWasteGeneratorStatus | null | undefined,
  acuteLbs: number | null | undefined,
  totalLbs: number | null | undefined
): boolean {
  return (
    federalStatus === RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator &&
    acuteLbs != null &&
    acuteLbs >= LQG_THRESHOLD_ACUTE_HAZARDOUS_LB &&
    (totalLbs == null || totalLbs < LQG_THRESHOLD_HAZARDOUS_LB)
  );
}

function formatLbs(value: number): string {
  return value.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/** Whole days between an ISO timestamp and now (>= 0). */
function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
}

type Props = {
  exceedance: ExceedanceForDrawer | null;
  /** Deep-link hydration in flight: open the shell with skeletons while the row loads. */
  loading?: boolean;
  onClose: () => void;
  /** Called after a resolution action so the caller can refetch + clear state. */
  onResolved: () => void;
};

export function ExceedanceActionDrawer({
  exceedance,
  loading = false,
  onClose,
  onResolved,
}: Props) {
  const open = exceedance !== null || loading;
  const [notePanel, setNotePanel] = useState<"dismiss" | "reset" | null>(null);
  const [noteText, setNoteText] = useState("");
  const [fileDialogOpen, setFileDialogOpen] = useState(false);
  const [markResolvedOpen, setMarkResolvedOpen] = useState(false);

  const { data, loading: detailLoading } = useQuery(DETAIL_QUERY, {
    variables: exceedance
      ? {
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: exceedance.periodStart,
        }
      : { facilityId: "", source: "", periodStart: "" },
    skip: !exceedance,
    fetchPolicy: "cache-and-network",
  });

  const [dismiss, { loading: dismissing }] = useMutation(DISMISS_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [reopen, { loading: reopening }] = useMutation(REOPEN_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const [validate, { loading: validating }] = useMutation(VALIDATE_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const [unvalidate, { loading: unvalidating }] = useMutation(UNVALIDATE_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });

  const alerts = useAlerts();

  const busy = dismissing || reopening || validating || unvalidating;

  // Validate keeps the drawer open (you often validate, then file). The parent passes
  // a snapshot, so we overlay the changed field locally and refetch the grid behind the
  // scenes; the overlay resets when a different row opens.
  const [overlay, setOverlay] = useState<{
    validatedAt?: string | null;
  }>({});
  // The months table collapses long histories behind a toggle (no nested scroll
  // region — the drawer already scrolls). Collapse again when a new row opens.
  const [showAllMonths, setShowAllMonths] = useState(false);
  useEffect(() => {
    setOverlay({});
    setShowAllMonths(false);
  }, [exceedance?.facilityId, exceedance?.source, exceedance?.periodStart]);
  const effectiveValidatedAt =
    "validatedAt" in overlay ? overlay.validatedAt : (exceedance?.validatedAt ?? null);

  const exceedanceKey = useMemo(
    () =>
      exceedance
        ? {
            facilityId: exceedance.facilityId,
            source: exceedance.source,
            periodStart: exceedance.periodStart,
            reportedFederalStatus: exceedance.reportedFederalStatus ?? null,
            calculatedFederalStatus: exceedance.calculatedFederalStatus ?? null,
          }
        : null,
    [exceedance]
  );

  const detail = data?.generatorStatusExceedanceDetail;
  const federalFactors = (detail?.determinationFactors as { federal?: Record<string, number> })
    ?.federal;

  // Builds a same-page href that swaps the drawer to a sibling exceedance via its
  // deep-link param, preserving every other query param (omnisearch filters etc.).
  // The dashboard hydrates the new key and swaps the drawer in place.
  const [pageSearchParams] = useSearchParams();
  const siblingDrawerSearch = (siblingPeriodStart: string): string => {
    const next = new URLSearchParams(pageSearchParams);
    if (exceedance) {
      next.set(
        DRAWER_PARAM,
        toDrawerParam({
          facilityId: exceedance.facilityId,
          source: exceedance.source,
          periodStart: siblingPeriodStart,
        })
      );
    }
    return `?${next.toString()}`;
  };
  // Acute-driven determination: the run's latest month is LQG on acute waste alone.
  // Called out prominently — a facility at a few hundred total lbs reads as
  // obviously-not-LQG unless the 1 kg acute rule is front and center.
  const factorAcuteLbs = federalFactors?.["Acute Waste (lbs)"] ?? null;
  const factorTotalLbs = federalFactors?.["Total Hazardous Waste (lbs)"] ?? null;
  const acuteDriven = isAcuteDriven(
    exceedance?.calculatedFederalStatus,
    factorAcuteLbs,
    factorTotalLbs
  );

  const closeAndReset = () => {
    setNotePanel(null);
    setNoteText("");
    onClose();
  };

  const runDismiss = async () => {
    if (!exceedanceKey) return;
    try {
      await dismiss({ variables: { exceedances: [exceedanceKey], reason: noteText } });
      alerts.success("Discrepancy dismissed.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to dismiss the discrepancy.", e);
    }
  };

  // Reopen/reset back to UNRESOLVED — also unlinks any linked submission. The
  // optional reason is recorded on the audit event.
  const runReset = async () => {
    if (!exceedanceKey) return;
    try {
      await reopen({ variables: { exceedances: [exceedanceKey], note: noteText || undefined } });
      alerts.success(linkedOrOpenSubmission ? "Unlinked & reset to unresolved." : "Reopened.");
      setNotePanel(null);
      setNoteText("");
      onResolved();
    } catch (e) {
      alerts.error("Failed to reset the discrepancy.", e);
    }
  };

  // Validate (TPM) — marks ready for pickup; keeps the drawer open so you can then
  // file. Mirrors the bulk "Validate (mark ready for pickup)" action.
  const runValidate = async () => {
    if (!exceedanceKey) return;
    try {
      await validate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validated — ready for pickup.");
      setOverlay((o) => ({ ...o, validatedAt: new Date().toISOString() }));
    } catch (e) {
      alerts.error("Failed to validate the discrepancy.", e);
    }
  };

  // Un-validate (TPM) — undo a validation applied in error; the row drops back to
  // "Needs Validation". Keeps the drawer open so the footer re-renders to the triage
  // fork, mirroring runValidate (which keeps it open to then file).
  const runUnvalidate = async () => {
    if (!exceedanceKey) return;
    try {
      await unvalidate({ variables: { exceedances: [exceedanceKey] } });
      alerts.success("Validation undone — back to needs validation.");
      setOverlay((o) => ({ ...o, validatedAt: null }));
    } catch (e) {
      alerts.error("Failed to undo the validation.", e);
    }
  };

  const isResolved =
    exceedance?.resolutionStatus === ResolutionStatus.Resolved ||
    exceedance?.resolutionStatus === ResolutionStatus.Ignored;
  const isUnresolved = exceedance?.resolutionStatus === ResolutionStatus.Unresolved;
  const isValidated = isUnresolved && Boolean(effectiveValidatedAt);
  const linkedOrOpenSubmission = exceedance?.linkedSubmission ?? exceedance?.openSubmission ?? null;
  // A linked/open submission means the reset also unlinks it.
  const resetLabel = linkedOrOpenSubmission ? "Unlink & reset" : "Reopen";

  const facilityForFiling: GeneratorStatusChangeFacility[] = exceedance
    ? [
        {
          facilityId: exceedance.facilityId,
          tenantId: exceedance.tenantId,
          facilityName: exceedance.facilityName,
          organization: exceedance.organization,
          state: exceedance.state,
          periodStart: exceedance.periodStart,
        },
      ]
    : [];

  // Footer actions are stage-specific. Ownership is NOT here — it lives in the header Owner
  // control. Each stage shows the one decision it's asking for; escapes demote to quiet links.
  const renderFooterActions = () => {
    if (notePanel) {
      return (
        <Stack spacing={1}>
          <TextField
            label={
              notePanel === "dismiss"
                ? "Reason (why is this not a discrepancy?)"
                : `Reason (optional)${linkedOrOpenSubmission ? " — this unlinks the submission" : ""}`
            }
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            size="small"
            multiline
            minRows={2}
            autoFocus
          />
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={() => setNotePanel(null)} disabled={busy}>
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              onClick={notePanel === "dismiss" ? runDismiss : runReset}
              loading={notePanel === "dismiss" ? dismissing : reopening}
              disabled={busy || (notePanel === "dismiss" && !noteText.trim())}
            >
              {notePanel === "dismiss" ? "Dismiss discrepancy" : "Reset to unresolved"}
            </LoadingButton>
          </Stack>
        </Stack>
      );
    }

    if (isResolved) {
      return (
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          Reopen
        </Button>
      );
    }

    // Needs Validation — the TPM triage fork: one decision, two opposite outcomes.
    if (isUnresolved && !isValidated) {
      return (
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Is this a real discrepancy?
          </Typography>
          <Stack direction="row" spacing={1}>
            <LoadingButton
              fullWidth
              variant="contained"
              onClick={runValidate}
              loading={validating}
              disabled={busy}
            >
              Validate
            </LoadingButton>
            <Button
              fullWidth
              variant="outlined"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
          <Button
            size="small"
            sx={{ alignSelf: "flex-start" }}
            onClick={() => setMarkResolvedOpen(true)}
            disabled={busy}
          >
            Already corrected elsewhere? Mark resolved
          </Button>
        </Stack>
      );
    }

    // Validated · Ready for Pickup — the forward action is filing; the rest are quiet escapes.
    if (isValidated) {
      return (
        <Stack spacing={1}>
          <Button variant="contained" onClick={() => setFileDialogOpen(true)} disabled={busy}>
            File notification
          </Button>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button size="small" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
              Mark resolved
            </Button>
            <LoadingButton
              size="small"
              onClick={runUnvalidate}
              loading={unvalidating}
              disabled={busy}
            >
              Un-validate
            </LoadingButton>
            <Button
              size="small"
              color="warning"
              onClick={() => setNotePanel("dismiss")}
              disabled={busy}
            >
              Dismiss
            </Button>
          </Stack>
        </Stack>
      );
    }

    // In flight (PENDING) — tracking the linked submission.
    return (
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button variant="outlined" onClick={() => setMarkResolvedOpen(true)} disabled={busy}>
          Mark resolved
        </Button>
        <Button variant="outlined" onClick={() => setNotePanel("reset")} disabled={busy}>
          {resetLabel}
        </Button>
      </Stack>
    );
  };

  const footer = exceedance ? <Stack spacing={1.5}>{renderFooterActions()}</Stack> : null;

  return (
    <>
      <RightDrawerShell
        open={open}
        onClose={closeAndReset}
        // Freeze the page behind the drawer: its month history can scroll far
        // longer than the grid, and two live scrollbars side by side made it
        // easy to scroll the wrong surface.
        disableScrollLock={false}
        title={
          exceedance ? (
            // Title doubles as a facility hyperlink so staff can jump to the
            // facility without closing the drawer — new tab via the little
            // external-link icon. (Deliberately the only facility link; the
            // grid's Facility column is plain text.)
            <Link
              to={`/o/${exceedance.tenantId}/waste/facilities/${exceedance.facilityId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {exceedance.facilityName}
              <OpenInNew fontSize="small" />
            </Link>
          ) : (
            <Skeleton width={220} />
          )
        }
        footer={footer}
      >
        {!exceedance && loading && (
          // Deep-link hydration: the row is still loading — sketch the drawer's
          // shape (status row, discrepancy block, months table) instead of a
          // dead pane.
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Skeleton variant="rounded" width={140} height={24} />
              <Skeleton width={180} />
            </Stack>
            <Skeleton width="70%" />
            <Skeleton variant="rounded" height={96} />
            <Skeleton variant="rounded" height={180} />
          </Stack>
        )}
        {exceedance && (
          <Stack spacing={2.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <ResolutionStatusChip
                status={exceedance.resolutionStatus}
                label={stageLabel({ ...exceedance, validatedAt: effectiveValidatedAt })}
                role={stageRole(exceedance.resolutionStatus, isValidated)}
              />
              {/* Compliance-risk badge: a sustained/recurring exceedance
                  escalates the longer its status run goes unaddressed. */}
              {exceedance.riskLevel && (
                <StatusChip
                  color={
                    exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                      ? "error"
                      : "warning"
                  }
                  label={
                    exceedance.riskLevel === GeneratorStatusExceedanceRiskLevel.High
                      ? "High risk"
                      : "Elevated risk"
                  }
                />
              )}
              {/* The LQG determination rests on the 1 kg acute rule, not tonnage —
                  surfaced at the top so it can't be missed while triaging. */}
              {acuteDriven && (
                <Tooltip
                  title={`≥ ${LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lbs (1 kg) of acutely hazardous waste in a month makes a facility LQG regardless of total volume.`}
                >
                  <span>
                    <StatusChip color="error" label="Acute waste–driven" />
                  </span>
                </Tooltip>
              )}
              <Typography variant="body2" color="text.secondary">
                {exceedance.organization} · {exceedance.state ?? "—"} ·{" "}
                {formatPeriodSpan(exceedance)}
              </Typography>
            </Stack>

            {/* Lifecycle metadata — first detected, aging, sustained-month run, and
                ownership. Gives the TPM/RS context without leaving the drawer. */}
            <Stack
              direction="row"
              spacing={2}
              flexWrap="wrap"
              sx={{ color: "text.secondary", typography: "body2" }}
            >
              <span>
                {exceedance.direction === GeneratorStatusExceedanceDirection.Downward
                  ? "Downward trend"
                  : "Exceedance"}
                {exceedance.mismatchMonths ? ` · ${exceedance.mismatchMonths}mo detected` : ""}
                {exceedance.sustainedMonths ? ` · ${exceedance.sustainedMonths}mo consecutive` : ""}
              </span>
              {exceedance.detectedAt && (
                <span>
                  First detected {formatDate(exceedance.detectedAt)} (
                  {daysSince(exceedance.detectedAt)}d open)
                </span>
              )}
              {effectiveValidatedAt && <span>Validated {formatDate(effectiveValidatedAt)}</span>}
            </Stack>

            {/* Owner — read-only. Assignment lives on the linked Generator Status Change
                Notification submission, not the exceedance, so it's set by
                owning that submission rather than from here. */}
            {exceedance.staffAssigneeName && (
              <Typography variant="body2" color="text.secondary">
                Owner: {exceedance.staffAssigneeName}
              </Typography>
            )}

            {/* Discrepancy */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Potential discrepancy
              </Typography>
              <Typography variant="body2">
                Reported{" "}
                <strong>
                  {exceedance.reportedFederalStatus
                    ? statusLabel(exceedance.reportedFederalStatus)
                    : "—"}
                </strong>{" "}
                → calculated{" "}
                <strong>
                  {exceedance.calculatedFederalStatus
                    ? statusLabel(exceedance.calculatedFederalStatus)
                    : "—"}
                </strong>
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                Calculated status is reconciled — the highest generator status across the latest
                month&apos;s e-Manifest, vendor &amp; reviewed-document data (no weight lost).
              </Typography>
              {acuteDriven && factorAcuteLbs != null && (
                <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                  <strong>Driven by acute hazardous waste.</strong> {formatLbs(factorAcuteLbs)} lbs
                  of acute waste this month — at or above the {LQG_THRESHOLD_ACUTE_HAZARDOUS_LB} lbs
                  (1 kg) acute threshold, which makes the facility LQG on its own
                  {factorTotalLbs != null
                    ? `; total volume (${formatLbs(factorTotalLbs)} lbs) is below the ${LQG_THRESHOLD_HAZARDOUS_LB.toLocaleString("en-US")} lbs LQG tonnage threshold`
                    : ""}
                  .
                </Alert>
              )}
              {/* Per-source breakdown for the run's latest month, as a real table so
                  it scans like the drawer's other sections instead of cramped lines. */}
              {detail && detail.sources.length > 0 && (
                <Table size="small" sx={{ mt: 1.5 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Source (
                        {formatPeriodMonth(exceedance.lastPeriodStart ?? exceedance.periodStart)})
                      </TableCell>
                      <TableCell>Calculated</TableCell>
                      <TableCell align="right">Total HW (lbs)</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.sources.map((s) => {
                      const used = s.federalStatus === exceedance.calculatedFederalStatus;
                      const sourceAcute = isAcuteDriven(
                        s.federalStatus,
                        s.acuteLbs,
                        s.totalHazardousLbs
                      );
                      return (
                        <TableRow key={s.source}>
                          <TableCell sx={{ fontWeight: used ? 600 : 400 }}>
                            {sourceLabel(s.source)}
                          </TableCell>
                          <TableCell sx={{ fontWeight: used ? 600 : 400 }}>
                            {s.federalStatus ? statusLabel(s.federalStatus) : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "text.secondary" }}>
                            {s.totalHazardousLbs != null ? formatLbs(s.totalHazardousLbs) : "—"}
                            {/* Only worth a mention when acute mass is present. */}
                            {s.acuteLbs != null && s.acuteLbs > 0
                              ? ` (${formatLbs(s.acuteLbs)} acute)`
                              : ""}
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>
                            {used && (
                              <StatusChip
                                color={sourceAcute ? "error" : "info"}
                                label={sourceAcute ? "drove status · acute" : "drove status"}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
              {/* Document-dominant facilities: the reconciled status came from reviewed
                  manifest-document data, which isn't persisted per-source — so no row
                  above is marked as driving the status. Say so, rather than leave the
                  breakdown looking incomplete. */}
              {detail &&
                exceedance.calculatedFederalStatus &&
                !detail.sources.some(
                  (s) => s.federalStatus === exceedance.calculatedFederalStatus
                ) && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    Reconciled from reviewed manifest-document data (not shown per-source above).
                  </Typography>
                )}
              {federalFactors && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: 0.6, lineHeight: 1 }}
                  >
                    Determination factors ·{" "}
                    {formatPeriodMonth(exceedance.lastPeriodStart ?? exceedance.periodStart)}
                  </Typography>
                  <Table size="small" sx={{ mt: 0.5 }}>
                    <TableBody>
                      {Object.entries(federalFactors).map(([k, v]) => {
                        // Bold the acute factor when it (alone) drove the LQG status.
                        const droveStatus = acuteDriven && k === "Acute Waste (lbs)";
                        return (
                          <TableRow key={k}>
                            <TableCell
                              sx={{
                                border: 0,
                                py: 0.25,
                                color: droveStatus ? "warning.main" : "text.secondary",
                                fontWeight: droveStatus ? 600 : 400,
                              }}
                            >
                              {k}
                            </TableCell>
                            <TableCell
                              sx={{ border: 0, py: 0.25, fontWeight: droveStatus ? 600 : 400 }}
                              align="right"
                            >
                              {v.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                              {droveStatus ? " — drove LQG" : ""}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              )}
              {exceedance.changeExplanation && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  Month over month: {exceedance.changeExplanation}
                </Typography>
              )}
            </Box>

            {/* The detail query fills in the per-source breakdown, month history,
                and audit trail. Its first load sketches those sections so the
                drawer's shape doesn't pop in after the fact. */}
            {!detail && detailLoading && (
              <Stack spacing={1}>
                <Skeleton variant="rounded" height={88} />
                <Skeleton variant="rounded" height={160} />
              </Stack>
            )}

            {/* Month-by-month calculated statuses — the audit trail behind the
                consolidated row. Bold + the chip mark the months that folded into
                THIS exceedance (same calculated status); other mismatching months
                belong to sibling dashboard rows at a different status. */}
            {detail && detail.calculatedMonths.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Calculated status by month
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Months at this discrepancy&apos;s calculated status roll up into the row
                  you&apos;re viewing; months at a different mismatched status appear as their own
                  dashboard row.
                </Typography>
                {/* No inner scroll region — a nested scrollbar inside the drawer's
                    own scroll reads as a "double scrollbar". Long histories collapse
                    behind a show-all toggle instead. */}
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell>Calculated</TableCell>
                      <TableCell align="right">Total HW (lbs)</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(showAllMonths
                      ? detail.calculatedMonths
                      : detail.calculatedMonths.slice(0, COLLAPSED_MONTHS)
                    ).map((m) => {
                      // Run-membership cell: informational, not a warning — plain
                      // text marks this row's own months (the bold row already
                      // carries the emphasis); for a month that mismatches under a
                      // DIFFERENT status, cross-link to the sibling row's drawer
                      // when it's materialized, plain text when it isn't (yet).
                      let runCell: ReactNode = null;
                      if (m.inRun) {
                        runCell = (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            this discrepancy
                          </Typography>
                        );
                      } else if (m.mismatch) {
                        runCell = m.siblingPeriodStart ? (
                          <MuiLink
                            component={Link}
                            to={{ search: siblingDrawerSearch(m.siblingPeriodStart) }}
                            variant="caption"
                            noWrap
                          >
                            separate discrepancy
                          </MuiLink>
                        ) : (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            separate discrepancy
                          </Typography>
                        );
                      }
                      const monthAcute = isAcuteDriven(
                        m.federalStatus,
                        m.acuteLbs,
                        m.totalHazardousLbs
                      );
                      return (
                        <TableRow key={m.periodStart}>
                          <TableCell sx={{ fontWeight: m.inRun ? 600 : 400 }}>
                            {formatPeriodMonth(m.periodStart)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: m.inRun ? 600 : 400,
                              color: m.mismatch ? "text.primary" : "text.secondary",
                            }}
                          >
                            {m.federalStatus ? statusLabel(m.federalStatus) : "—"}
                            {/* The month is LQG on the 1 kg acute rule, not tonnage. */}
                            {monthAcute && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ color: "warning.main", fontWeight: 600, ml: 0.5 }}
                              >
                                · acute
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: "text.secondary" }}>
                            {m.totalHazardousLbs != null ? formatLbs(m.totalHazardousLbs) : "—"}
                            {m.acuteLbs != null && m.acuteLbs > 0
                              ? ` (${formatLbs(m.acuteLbs)} acute)`
                              : ""}
                          </TableCell>
                          <TableCell sx={{ py: 0.25 }}>{runCell}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                {detail.calculatedMonths.length > COLLAPSED_MONTHS && (
                  <Button size="small" onClick={() => setShowAllMonths((v) => !v)} sx={{ mt: 0.5 }}>
                    {showAllMonths
                      ? "Show fewer"
                      : `Show all ${detail.calculatedMonths.length} months`}
                  </Button>
                )}
              </Box>
            )}

            {/* Linked submission — an explicitly linked submission (any type) or
                the auto-detected open notification driving the status. */}
            {linkedOrOpenSubmission && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Linked submission
                </Typography>
                <MuiLink
                  component={Link}
                  to={`/o/${exceedance.tenantId}/waste/submissions/${linkedOrOpenSubmission.id}/edit`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
                >
                  <OpenInNew fontSize="small" />
                  {linkedOrOpenSubmission.title} ({getStatusLabel(linkedOrOpenSubmission.status)})
                </MuiLink>
              </Box>
            )}

            {/* WADM accumulation — only rendered when the facility actually has
                hazardous WADM data. The WADM feed is Amazon-only, so this section
                is hidden entirely (no header, no empty state) for everyone else. */}
            {detail && detail.wadmByMonth.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  WADM — hazardous waste accumulation (month by month)
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Weight (lbs)</TableCell>
                      <TableCell align="right">Items</TableCell>
                      <TableCell>WADM status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.wadmByMonth.map((m) => (
                      <TableRow key={m.periodStart}>
                        <TableCell>{formatPeriodMonth(m.periodStart)}</TableCell>
                        <TableCell align="right">
                          {m.totalWeightLbs.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                        </TableCell>
                        <TableCell align="right">{m.itemCount}</TableCell>
                        <TableCell>{m.wadmGeneratorStatus ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* History */}
            {detail && detail.events.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Resolution history
                </Typography>
                <Stack spacing={0.5}>
                  {detail.events.map((ev) => (
                    <Typography key={ev.id} variant="caption" color="text.secondary">
                      {new Date(ev.createdAt).toLocaleString("en-US")} — {statusLabel(ev.eventType)}
                      {" by "}
                      <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                        {ev.actorName ?? "—"}
                      </Box>
                      {ev.note ? `: ${ev.note}` : ""}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {exceedance.resolutionStatus === ResolutionStatus.Pending && (
              <Alert severity="info" variant="outlined">
                A notification has been filed for this facility — this discrepancy is being worked.
              </Alert>
            )}
          </Stack>
        )}
      </RightDrawerShell>

      <CreateGeneratorStatusChangeSubmissionsDialog
        open={fileDialogOpen}
        onClose={() => setFileDialogOpen(false)}
        facilities={facilityForFiling}
        onSuccess={() => {
          setFileDialogOpen(false);
          onResolved();
        }}
      />

      {exceedanceKey && exceedance && (
        <MarkResolvedDialog
          open={markResolvedOpen}
          onClose={() => setMarkResolvedOpen(false)}
          exceedanceKey={exceedanceKey}
          facilityId={exceedance.facilityId}
          tenantId={exceedance.tenantId}
          onResolved={() => {
            setMarkResolvedOpen(false);
            onResolved();
          }}
        />
      )}
    </>
  );
}

type LinkableSubmissionOption = {
  id: string;
  title: string;
  submissionType: string;
  status: string;
  dueDate?: string | null;
  createdAt: string;
  facilityName?: string | null;
  assigneeName?: string | null;
};

type MarkResolvedDialogProps = {
  open: boolean;
  onClose: () => void;
  exceedanceKey: ExceedanceKey;
  facilityId: string;
  /** Builds the per-candidate submission link-outs. */
  tenantId: string;
  onResolved: () => void;
};

/**
 * "Mark resolved" dialog. Two paths:
 *  - Corrected elsewhere: acknowledge with an optional note, no submission.
 *  - Link to an existing submission: pick one of the facility's recent submissions
 *    (e.g. an in-progress notification for a consecutive-month exceedance, or an
 *    "Other"-typed memo to file). The exceedance then tracks that submission's
 *    status instead of being flatly "resolved". Candidates render as a grid with
 *    per-row link-outs so a TPM can open and verify a submission in a
 *    new tab before committing to the link.
 */
function MarkResolvedDialog({
  open,
  onClose,
  exceedanceKey,
  facilityId,
  tenantId,
  onResolved,
}: MarkResolvedDialogProps) {
  const [mode, setMode] = useState<"acknowledge" | "link">("acknowledge");
  const [note, setNote] = useState("");
  const [selected, setSelected] = useState<LinkableSubmissionOption | null>(null);
  // Server-side title search: the picker returns only the newest submissions,
  // so older ones are reachable by narrowing rather than paging.
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 300);

  const { data, loading: loadingOptions } = useQuery(LINKABLE_SUBMISSIONS_QUERY, {
    variables: { facilityId, search: debouncedSearch.trim() || undefined },
    skip: !open,
    fetchPolicy: "cache-and-network",
  });

  const [markResolved, { loading: submitting }] = useMutation(MARK_RESOLVED_MUTATION, {
    refetchQueries: REFETCH,
    awaitRefetchQueries: true,
  });
  const alerts = useAlerts();

  const options: LinkableSubmissionOption[] =
    data?.generatorStatusExceedanceLinkableSubmissions ?? [];

  // A search that filters the selected row out of the grid also clears the
  // selection — an invisible selection would otherwise still be submittable.
  useEffect(() => {
    if (selected && data && !options.some((o) => o.id === selected.id)) {
      setSelected(null);
    }
    // oxlint-disable-next-line exhaustive-deps
  }, [data]);

  const reset = () => {
    setMode("acknowledge");
    setNote("");
    setSelected(null);
    setSearch("");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      await markResolved({
        variables: {
          exceedances: [exceedanceKey],
          note: note.trim(),
          submissionId: mode === "link" ? selected?.id : undefined,
        },
      });
      alerts.success(mode === "link" ? "Linked to submission." : "Marked resolved.");
      reset();
      onResolved();
    } catch (e) {
      alerts.error("Failed to mark resolved.", e);
    }
  };

  // A note is required: it's the audit record of why/how this was resolved.
  const submitDisabled = submitting || (mode === "link" && !selected) || !note.trim();

  // Candidate-submissions grid: row click selects the submission to
  // link; the Actions column opens it in a new tab for verification first.
  const linkColumns = useMemo<CustomGridColDef<LinkableSubmissionOption>[]>(
    () => [
      { field: "title", headerName: "Title", flex: 1, minWidth: 180 },
      {
        field: "submissionType",
        headerName: "Type",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? getStatusLabel(String(value)) : ""),
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 0.5,
        valueFormatter: (value) =>
          value != null ? new Date(String(value)).toLocaleDateString("en-US") : "",
      },
      {
        field: "assigneeName",
        headerName: "Assignee",
        flex: 0.6,
        valueGetter: (_, row) => row.assigneeName ?? "Unassigned",
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open submission in a new tab">
            {/* One interactive element (a button that IS the link) — a button nested
                inside an anchor is invalid and double-stops keyboard focus. */}
            <IconButton
              size="small"
              component={Link}
              to={`/o/${tenantId}/waste/submissions/${params.row.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open submission in a new tab"
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      slotProps={{ paper: { sx: { width: 860, maxWidth: "95vw" } } }}
      title="Mark discrepancy resolved"
      description="Acknowledge this discrepancy, or link it to an existing submission so its status tracks that filing."
      primaryActionButtonProps={{
        onClick: handleSubmit,
        loading: submitting,
        loadingText: "Saving...",
        hideStartIcon: true,
        // Linking moves the discrepancy to PENDING (it tracks the submission's
        // lifecycle) — only the acknowledge path resolves it outright, so the
        // button says which of the two is about to happen.
        saveText: mode === "link" ? "Link submission" : "Mark resolved",
        disabled: submitDisabled,
      }}
      cancelButtonProps={{ disabled: submitting }}
    >
      <RadioGroup
        value={mode}
        onChange={(e) => setMode(e.target.value === "link" ? "link" : "acknowledge")}
      >
        <FormControlLabel
          value="acknowledge"
          control={<Radio size="small" />}
          label="Corrected elsewhere — no submission needed"
        />
        <FormControlLabel
          value="link"
          control={<Radio size="small" />}
          label="Link to an existing submission"
        />
      </RadioGroup>

      {mode === "link" && (
        <Box sx={{ mt: 1 }}>
          <TextField
            label="Search submissions by title"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{ mb: 1 }}
          />
          <DataGrid<LinkableSubmissionOption>
            rows={options}
            columns={linkColumns}
            loading={loadingOptions}
            noRowsMessage={
              debouncedSearch.trim()
                ? "No submissions match that title."
                : "No recent submissions for this facility."
            }
            // Visible single-select: a checkbox per row (the select-all header
            // checkbox is hidden below) so the current pick is unmissable —
            // row-highlight-only selection was easy to miss and cleared silently.
            checkboxSelection
            disableMultipleRowSelection
            rowSelectionModel={{
              type: "include",
              ids: new Set(selected ? [selected.id] : []),
            }}
            onRowSelectionModelChange={(model) => {
              const [id] = model.type === "include" ? Array.from(model.ids) : [];
              setSelected(options.find((o) => o.id === id) ?? null);
            }}
            pagination
            paginationMode="client"
            pageSizeOptions={[5, 10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
            sx={{
              maxHeight: 360,
              // Single-select: hide the header's select-all checkbox (same
              // treatment as OmnisearchDataGrid's disableSelectAll).
              "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                display: "none",
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Check the submission to link; use the open-in-new-tab icon to verify it first.
          </Typography>
        </Box>
      )}

      <TextField
        label="Note (required)"
        required
        value={note}
        onChange={(e) => setNote(e.target.value)}
        size="small"
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 2 }}
      />
    </Dialog>
  );
}
